import {
  ApiError,
  ERROR_CODES,
  type ApiErrorDetails,
} from '@luacentrix/shared';
import { SCRIPT_UPLOAD_FILE_FIELD } from '@luacentrix/contracts';
import { parseErrorEnvelope } from './envelope';
import type { ParsedEnvelope } from './envelope';
import { buildHeaders, newIdempotencyKey, newRequestId } from './transport';

export const SCRIPT_UPLOAD_COLLECTION_PATH = '/api/v1/scripts/uploads';

export function scriptUploadItemPath(uploadId: string): string {
  return `${SCRIPT_UPLOAD_COLLECTION_PATH}/${encodeURIComponent(uploadId)}`;
}

export const UPLOAD_TIMEOUT_MS = 120_000;

export type CredentialsMode = 'include' | 'same-origin' | 'omit';

export interface UploadProgress {
  sentBytes: number;
  totalBytes: number;
  ratio: number;
}

export interface XhrUploadEvent {
  loaded: number;
  total: number;
  lengthComputable: boolean;
}

export interface XhrLike {
  status: number;
  responseText: string;
  timeout: number;
  withCredentials: boolean;
  upload: { onprogress: ((event: XhrUploadEvent) => void) | null };
  onload: (() => void) | null;
  onerror: (() => void) | null;
  ontimeout: (() => void) | null;
  onabort: (() => void) | null;
  open(method: string, url: string, async: boolean): void;
  setRequestHeader(name: string, value: string): void;
  send(body: FormData): void;
  abort(): void;
}

export type XhrFactory = () => XhrLike;

export interface UploadTransportInput {
  baseUrl: string;
  path: string;
  file: File;
  fileField?: string | undefined;
  accessToken?: string | undefined;
  credentials?: CredentialsMode | undefined;
  timeoutMs?: number | undefined;
  requestId?: string | undefined;
  idempotencyKey?: string | undefined;
  signal?: AbortSignal | undefined;
  onProgress?: ((progress: UploadProgress) => void) | undefined;
  createRequest: XhrFactory;
}

export function uploadProgressOf(event: XhrUploadEvent, fileBytes: number): UploadProgress {
  const total = event.lengthComputable && event.total > 0 ? event.total : fileBytes;
  const sent = Math.max(0, Math.min(event.loaded, total));
  return { sentBytes: sent, totalBytes: total, ratio: total > 0 ? sent / total : 0 };
}

function statusFailure(payload: unknown, status: number, requestId: string): ApiError {
  let envelope: ParsedEnvelope;
  try {
    envelope = parseErrorEnvelope(payload);
  } catch {
    return new ApiError(ERROR_CODES.SERVICE_UNAVAILABLE, {
      details: { request_id: requestId, reason: 'invalid_error_envelope', http_status: status },
    });
  }
  const details: ApiErrorDetails = {
    ...(envelope.details ?? {}),
    http_status: status,
    request_id: envelope.requestId ?? requestId,
  };
  return new ApiError(envelope.code, { message: envelope.message, details, status });
}

function aborted(requestId: string): ApiError {
  return new ApiError(ERROR_CODES.REQUEST_TIMEOUT, {
    details: { request_id: requestId, reason: 'aborted_by_caller' },
  });
}

function parseJson(text: string): unknown | null {
  if (text === '') {
    return {};
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

export function sendScriptUpload<T>(input: UploadTransportInput): Promise<T> {
  const requestId = input.requestId ?? newRequestId();
  const idempotencyKey = input.idempotencyKey ?? newIdempotencyKey(input.path);
  const timeoutMs = input.timeoutMs ?? UPLOAD_TIMEOUT_MS;
  const origin = input.baseUrl.replace(/\/+$/, '');
  const { headers } = buildHeaders({
    requestId,
    idempotencyKey,
    ...(input.accessToken === undefined ? {} : { accessToken: input.accessToken }),
    hasBody: false,
  });

  const form = new FormData();
  form.set(input.fileField ?? SCRIPT_UPLOAD_FILE_FIELD, input.file);

  return new Promise<T>((resolve, reject) => {
    const xhr = input.createRequest();
    let settled = false;

    const onAbortSignal = (): void => {
      xhr.abort();
      if (settled) {
        return;
      }
      settled = true;
      input.signal?.removeEventListener('abort', onAbortSignal);
      reject(aborted(requestId));
    };

    const settle = (failure: ApiError | null, value?: T): void => {
      if (settled) {
        return;
      }
      settled = true;
      input.signal?.removeEventListener('abort', onAbortSignal);
      if (failure === null) {
        resolve(value as T);
        return;
      }
      reject(failure);
    };

    xhr.open('POST', `${origin}${input.path}`, true);
    xhr.withCredentials = (input.credentials ?? 'include') !== 'omit';
    xhr.timeout = timeoutMs;
    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value);
    }

    xhr.upload.onprogress = event => {
      if (settled) {
        return;
      }
      input.onProgress?.(uploadProgressOf(event, input.file.size));
    };
    xhr.onload = () => {
      const status = xhr.status;
      const payload = parseJson(xhr.responseText);
      if (status < 200 || status >= 300) {
        settle(
          payload === null
            ? new ApiError(ERROR_CODES.SERVICE_UNAVAILABLE, {
                details: {
                  request_id: requestId,
                  reason: 'invalid_error_envelope',
                  http_status: status,
                },
              })
            : statusFailure(payload, status, requestId),
        );
        return;
      }
      if (payload === null) {
        settle(
          new ApiError(ERROR_CODES.UNSUPPORTED_SERVER_STATE, {
            details: { field: 'response_body', reason: 'invalid_json', http_status: status },
          }),
        );
        return;
      }
      settle(null, payload as T);
    };
    xhr.onerror = () => {
      settle(
        new ApiError(ERROR_CODES.SERVICE_UNAVAILABLE, {
          details: { request_id: requestId, reason: 'network_failure' },
        }),
      );
    };
    xhr.ontimeout = () => {
      settle(
        new ApiError(ERROR_CODES.REQUEST_TIMEOUT, {
          details: { request_id: requestId, reason: 'deadline_exceeded' },
        }),
      );
    };
    xhr.onabort = () => {
      settle(aborted(requestId));
    };

    if (input.signal !== undefined) {
      if (input.signal.aborted) {
        settle(aborted(requestId));
        return;
      }
      input.signal.addEventListener('abort', onAbortSignal);
    }

    try {
      xhr.send(form);
    } catch (reason) {
      settle(
        new ApiError(ERROR_CODES.SERVICE_UNAVAILABLE, {
          details: {
            request_id: requestId,
            reason: reason instanceof Error ? reason.name : 'send_failed',
          },
        }),
      );
    }
  });
}
